/*
Site Video
*/

class SiteVideo extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ("IntersectionObserver" in window) {
      const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (video) {
          const $video = video.target;
          if (video.isIntersecting) {
            if ($video.readyState === 4) {
              $video.play();
            } else {
              [].slice
                .call($video.querySelectorAll("source"))
                .forEach((source) => {
                  source.src = source.dataset.src;
                });
              $video.load();
            }
          } else {
            if (!$video.paused) {
              $video.pause();
            }
          }
        });
      });

      videoObserver.observe(this.querySelector("video"));
    }
  }
}

customElements.define("site-video", SiteVideo);

/*
Site Image
*/

class SiteImage extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((el) => {
          if (el.isIntersecting) {
            const srcset =
              el.target.dataset.srcset || el.target.getAttribute("srcset");
            const preloadedImage = new Image();
            preloadedImage.srcset = srcset;
            preloadedImage.onload = () => {
              el.target.srcset = srcset;
              this.classList.add("active");
              imageObserver.unobserve(this);
            };
          }
        });
      });

      const img = this.querySelector(".image-full");
      imageObserver.observe(img);
    }
  }
}

customElements.define("site-image", SiteImage);

/*
Anim Fade
*/

class AnimFade extends HTMLElement {
  constructor() {
    super();
    this.options = {
      threshold: 0.75,
    };
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      this.options
    );
  }

  connectedCallback() {
    this.targetElement = this.firstElementChild;

    if (!this.targetElement) {
      console.error("Target element not found");
      return;
    }

    this.targetElement.style.opacity = 0;
    this.targetElement.style.transform = "translateY(60px)";
    this.targetElement.style.transition = "all 1s ease";

    this.observer.observe(this.targetElement);
  }

  handleIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.targetElement.style.opacity = 1;
        this.targetElement.style.transform = "translateY(0px)";
      } else {
        this.targetElement.style.opacity = 0;
        this.targetElement.style.transform = "translateY(60px)";
      }
    });
  }
}

customElements.define("anim-fade", AnimFade);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");
      console.log(carousel);
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    const config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 3, spaceBetween: 20 };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, config);
    this.handleSlideChange();
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    // we can have multiple pagers in one carousel
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button"));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove("active"));
          nextPager.classList.add("active");
        });
      });
    }
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Flyout Toggle
*/

class FlyoutToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Flyout toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const flyout = this.closest("site-flyout");
      const header = document.querySelector("site-header");
      if (flyout) {
        if (flyout.classList.contains("active")) {
          flyout.close();
          header.cartOrFlyoutClose("flyout");
        } else {
          flyout.open();
          header.cartOrFlyoutOpen("flyout");
        }
      }
    });
  }
}

customElements.define("flyout-toggle", FlyoutToggle);

/*
Site Flyout
*/

class SiteFlyout extends HTMLElement {
  constructor() {
    super();
    this.flyout = null;
  }

  connectedCallback() {
    this.flyout = this.closest("site-flyout");
  }

  open() {
    document.body.style.overflow = "hidden";
    this.flyout.classList.add("active");
  }

  close() {
    document.body.removeAttribute("style");
    this.flyout.classList.remove("active");
  }
}

customElements.define("site-flyout", SiteFlyout);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Filters Toggle
*/

class FiltersToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Filters toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const filters = document.querySelector("product-filters");
      if (filters) {
        filters.toggleFilters();
      }
    });
  }
}

customElements.define("filters-toggle", FiltersToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector("form").addEventListener("submit", async (e) => {
      e.preventDefault();

      const $id = e.target.querySelector('[name="id"]');
      const $qty = e.target.querySelector('[name="quantity"]');
      const $sellingPlan = e.target.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value : null;

      if ($id) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          await cart.addVariantToCart($id.value, qty, sellingPlan);
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Cart Update
*/

class CartUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "change",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = e.target.dataset.index;
        const val = e.target.value;

        if (cart) {
          await cart.updateQuantity(idx, val);
        }
      }, 300)
    );
  }
}

customElements.define("cart-update", CartUpdate);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.header = null;
    this.isMobile = false;
  }

  connectedCallback() {
    this.header = this.querySelector("header");
    this.isInverted =
      this.header.classList.contains("inverted") ||
      this.header.classList.contains("custom-inverted");
    this.checkIfMobile();
  }

  checkIfMobile() {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleViewportChange = (mq) => {
      if (mq.matches) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    };
    handleViewportChange(mediaQuery);
    mediaQuery.addListener(handleViewportChange);

    const currPage = window.location.href;
    if (currPage.includes("blog")) {
      console.log(true);
    }
  }

  cartOrFlyoutClose(ctx) {
    const menuToggle = this.header.querySelector('[data-trigger="toggle-menu"');

    if (this.isMobile) {
      if (this.isInverted) {
        this.header.classList.add("inverted");
      }

      if (ctx === "cart") {
        this.header
          .querySelector('[data-trigger="close-cart"')
          .classList.add("hidden");

        menuToggle.classList.remove("hidden");
      }
      if (ctx === "flyout") {
        menuToggle
          .querySelector("span:nth-child(1)")
          .classList.remove("hidden");
        menuToggle.querySelector("span:nth-child(2)").classList.add("hidden");
      }
      if (ctx === "filters") {
        this.header
          .querySelector('[data-trigger="close-filters"]')
          .classList.add("hidden");

        menuToggle.classList.remove("hidden");
      }
    }
  }

  cartOrFlyoutOpen(ctx) {
    const menuToggle = this.header.querySelector('[data-trigger="toggle-menu"');

    if (this.isMobile) {
      this.header.classList.remove("inverted");

      if (ctx === "cart") {
        this.header
          .querySelector('[data-trigger="close-cart"')
          .classList.remove("hidden");

        menuToggle.classList.add("hidden");
      }
      if (ctx === "flyout") {
        menuToggle.querySelector("span:nth-child(1)").classList.add("hidden");
        menuToggle
          .querySelector("span:nth-child(2)")
          .classList.remove("hidden");
      }
      if (ctx === "filters") {
        this.header
          .querySelector('[data-trigger="close-filters"]')
          .classList.remove("hidden");

        menuToggle.classList.add("hidden");
      }
    }
  }
}

customElements.define("site-header", SiteHeader);

/*
Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
    this.handleRewards();
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
      document.body.style.overflow = "hidden";
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
      document.body.removeAttribute("style");
    }
  }

  toggleCart() {
    const header = document.querySelector("site-header");
    if (this.cart.classList.contains("active")) {
      this.closeCart();
      header.cartOrFlyoutClose("cart");
    } else {
      this.openCart();
      header.cartOrFlyoutOpen("cart");
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
      {
        section: "cart-icon-bubble",
        selector: "#cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        elToReplace.innerHTML = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector
        );
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, sellingPlan) {
    const items = [
      sellingPlan
        ? { id, quantity, selling_plan: sellingPlan }
        : { id, quantity },
    ];
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (!this.cart.classList.contains("active")) {
      this.toggleCart();
    }

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
        this.handleRewards();
      })
      .catch((e) => {
        // handle error
      });
  }

  updateQuantity(line, quantity) {
    const items = Array.from(document.querySelectorAll("cart-item"));
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);
    const item = items[line - 1];
    const price = item.querySelector(".cart-price");
    const loader = item.querySelector(".cart-loader");

    if (price) {
      price.classList.add("hidden");
    }
    if (loader) {
      loader.classList.remove("hidden");
    }

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then((response) => {
        if (price) {
          price.classList.remove("hidden");
        }
        if (loader) {
          loader.classList.add("hidden");
        }

        this.renderCartSections(response.sections);
        this.handleRewards();
      })
      .catch(() => {
        // handle error
      });
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return await resp.json();
  }

  async handleRewards() {
    const cart = await this.fetchCart();
    const rewards = this.querySelector("cart-rewards");
    const thresholdPrice = rewards ? Number(rewards.dataset.threshold) : 0;
    const subtotal = cart.items.reduce((acc, item) => {
      return (acc += item.original_line_price);
    }, 0);

    const statuses = Array.from(rewards.querySelectorAll("[data-status]"));
    const hideStatuses = () => {
      statuses.forEach((status) => {
        status.classList.add("hidden");
      });
    };

    hideStatuses();

    if (subtotal === 0) {
      const empty = rewards.querySelector('[data-status="empty"]');
      empty.classList.remove("hidden");
    } else if (subtotal >= thresholdPrice) {
      const earned = rewards.querySelector('[data-status="earned"]');
      const rewardVariantId = rewards.dataset.variantId;
      const isInCart = cart.items.some(
        (item) => item.variant_id === Number(rewardVariantId)
      );

      earned.classList.remove("hidden");

      if (!isInCart) {
        this.addVariantToCart(rewardVariantId);
      }
    } else {
      const pending = rewards.querySelector('[data-status="pending"]');
      const remaining = pending.querySelector("span");
      const remainingAmount = thresholdPrice - subtotal;
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const fmtValue = formatter.format(remainingAmount / 100);

      pending.classList.remove("hidden");
      remaining.innerHTML = fmtValue;
    }

    const bar = rewards.querySelector(".percentage-bar");
    const width = (subtotal / thresholdPrice) * 100;
    bar.style.width = `${width}%`;
  }
}

customElements.define("site-cart", SiteCart);

/*
Cart Footer
 */

/*
Settings Selector
 */

class SettingSelector extends HTMLElement {
  constructor() {
    super();
    this.setupSelectors();
  }

  setupSelectors() {
    const options = this.querySelectorAll(".setting-option");
    const containers = this.querySelectorAll(".setting-container");

    containers.forEach((container) => {
      container.classList.add("hidden", "lg:hidden", "opacity-0");
    });

    options[0].classList.add("active");
    containers[0].classList.remove("opacity-0", "hidden", "lg:hidden");

    options.forEach((option) => {
      option.addEventListener("click", (e) => {
        options.forEach((option) => {
          option.classList.remove("active");
          if (e.target.dataset.setting === option.dataset.setting) {
            option.classList.add("active");
          }
        });
        if (option.classList.contains("active")) {
          const activeOption = option.dataset.setting;
          containers.forEach((container) => {
            if (container.dataset.setting == activeOption) {
              container.classList.remove("opacity-0", "hidden", "lg:hidden");
            } else {
              container.classList.add("hidden", "lg:hidden", "opacity-0");
            }
          });
        }
        if (option.classList.contains("view-order")) {
          const firstDetails = document.querySelector("details");
          firstDetails.open = true;
        }
      });
    });
  }
}
customElements.define("setting-selector", SettingSelector);

/*
FAQs
*/

class Faqs extends HTMLElement {
  constructor() {
    super();
    this.renderAnswer();
  }

  removeActiveClassFromOtherElements(clickedContainer) {
    const containers = document.querySelectorAll(".faqs__item-container");
    containers.forEach((container) => {
      if (
        container !== clickedContainer &&
        container.classList.contains("active")
      ) {
        container.classList.remove("active");
        container.querySelector(".faqs__answer").style.maxHeight = "0";
        container.querySelector(".faqs__item-plus").classList.remove("hidden");
        container.querySelector(".faqs__item-minus").classList.add("hidden");
      }
    });
  }

  renderAnswer() {
    const question = this.querySelector(".faqs__item");
    const container = this.querySelector(".faqs__item-container");
    const answer = this.querySelector(".faqs__answer");
    const plus = this.querySelector(".faqs__item-plus");
    const minus = this.querySelector(".faqs__item-minus");

    question.addEventListener("click", () => {
      this.removeActiveClassFromOtherElements(container);
      container.classList.toggle("active");

      if (container.classList.contains("active")) {
        answer.style.maxHeight = answer.scrollHeight + "px";
        plus.classList.add("hidden");
        minus.classList.remove("hidden");
      } else {
        answer.style.maxHeight = "0";
        plus.classList.remove("hidden");
        minus.classList.add("hidden");
      }
    });
  }
}
customElements.define("question-answer", Faqs);

/*
Login Form
 */

class LoginForm extends HTMLElement {
  constructor() {
    super();
    this.forgotPass();
  }
  forgotPass() {
    const loginForm = this.querySelector(".loginForm");
    const recoverForm = this.querySelector(".recover-form");
    const recoverBtn = this.querySelector(".recover-button");
    const closeBtn = this.querySelector(".close-button");
    const loginText = this.querySelector(".login-text");
    const recoverText = this.querySelector(".recover-text");

    recoverBtn.addEventListener("click", () => {
      recoverForm.classList.remove("hidden");
      recoverText.classList.remove("hidden");
      loginForm.classList.add("hidden");
      loginText.classList.add("hidden");
    });
    closeBtn.addEventListener("click", () => {
      recoverForm.classList.add("hidden");
      recoverText.classList.add("hidden");
      loginText.classList.remove("hidden");
      loginForm.classList.remove("hidden");
    });
  }
}

customElements.define("login-form", LoginForm);

/*
StickyATC
*/

class StickyAtc extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.showBar();
  }

  showBar() {
    const infoBar = this.querySelector("[data-modal='info-bar']");
    const stickyBar = this.querySelector("[data-modal='atc-bar']");

    window.addEventListener("scroll", () => {
      if (window.scrollY >= 1000) {
        stickyBar.classList.remove("opacity-0");
        stickyBar.style.visibility = "visible";
      } else {
        stickyBar.classList.add("opacity-0");
        stickyBar.style.visibility = "hidden";
      }
    });

    this.addEventListener("click", (e) => {
      const btn = e.target.closest("button") || e.target.closest("p");
      if (btn.dataset.modal === "open") {
        stickyBar.classList.add("opacity-0");
        stickyBar.style.visibility = "hidden";
        infoBar.classList.remove("opacity-0");
        infoBar.style.visibility = "visible";
      }
      if (btn.dataset.modal === "close") {
        stickyBar.classList.remove("opacity-0");
        stickyBar.style.visibility = "visible";
        infoBar.classList.add("opacity-0");
        infoBar.style.visibility = "hidden";
      }
    });
  }
}

customElements.define("sticky-atc", StickyAtc);

/*
Site Modal
*/

class SiteModal extends HTMLElement {
  constructor() {
    super();
    this.autoOpenTimeout = this.dataset.autoOpenTimeout;
  }

  openModal() {
    this.querySelector(".modal").classList.add("active");
  }

  closeModal() {
    this.querySelector(".modal").classList.remove("active");
  }

  connectedCallback() {
    const opens = this.querySelectorAll(".modal-open");
    const closes = this.querySelectorAll(".modal-close");
    const triggerClass = this.dataset.trigger;
    const trigger = document.querySelector(triggerClass);

    if (trigger) {
      trigger.addEventListener("click", (e) => {
        this.openModal();
      });
    }

    Array.from(opens).forEach((open) => {
      open.addEventListener("click", (e) => {
        this.openModal();
      });
    });

    Array.from(closes).forEach((close) => {
      close.addEventListener("click", (e) => {
        this.closeModal();
      });
    });
  }
}
customElements.define("site-modal", SiteModal);

/*
Select Input
*/

class SelectInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector("select").addEventListener("change", (e) => {
      this.setActiveValue(e.target.value);
    });
  }

  setActiveValue(val) {
    const options = Array.from(this.querySelectorAll("option"));
    const activeOption = options.find((o) => o.value === val);
    const target = this.querySelector(".select-target");

    if (activeOption) {
      target.innerHTML = activeOption.innerHTML;
    }
  }
}

customElements.define("select-input", SelectInput);

/*
Country Select
*/

class CountrySelect extends SelectInput {
  constructor() {
    super();
    this.select = null;
    this.activeOption = null;
  }

  connectedCallback() {
    this.select = this.querySelector("select");
    const active = this.dataset.active;

    if (active) {
      this.setActiveValue(active);
    }

    this.select.addEventListener("change", (e) => {
      const provinceSelect = document.querySelector("province-select");

      this.setActiveValue(e.target.value);
      provinceSelect.renderOptions();
    });
  }

  setActiveValue(val) {
    const provinceSelect = document.querySelector("province-select");
    const target = this.querySelector(".select-target");
    const options = Array.from(this.querySelectorAll("option"));

    this.activeOption = options.find((o) => o.value === val);

    if (this.activeOption) {
      this.select.value = this.activeOption.value;
      target.innerHTML = this.activeOption.value;
    }
  }
}

customElements.define("country-select", CountrySelect);

/*
Province Select
*/

class ProvinceSelect extends SelectInput {
  constructor() {
    super();
    this.select = null;
    this.target = null;
  }

  connectedCallback() {
    this.select = this.querySelector("select");
    this.target = this.querySelector(".select-target");
    this.renderOptions();
    this.setActive();
  }

  renderOptions() {
    const countrySelect = document.querySelector("country-select");
    const provinces = JSON.parse(countrySelect.activeOption.dataset.provinces);

    this.select.innerHTML = "";
    this.target.innerHTML = "";

    if (provinces.length > 0) {
      this.select.required = true;
      this.style.display = "block";

      provinces.forEach((province) => {
        const option = document.createElement("option");
        option.value = province[0];
        option.innerHTML = province[1];
        this.select.appendChild(option);
      });
    } else {
      this.select.required = false;
      this.style.display = "none";
    }
  }

  setActive() {
    const active = this.dataset.active;
    if (active) {
      this.target.innerHTML = active;
      this.select.value = active;
    }
  }
}

customElements.define("province-select", ProvinceSelect);

/*
Nutrient Selector
 */

class NutrientSelector extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.setupListeners();
  }

  hideAll(selector) {
    this.querySelectorAll(selector).forEach((s) => {
      s.classList.remove("block");
      s.classList.add("hidden");
    });
  }
  disableNutrientDesc() {
    this.querySelectorAll("[data-nutrient-desc]").forEach((d) => {
      d.classList.remove("first:font-heading-bold", "font-heading-bold");
      d.classList.add("font-heading-regular");
    });
  }
  hideDesc() {
    this.querySelectorAll("[data-desc]").forEach((s) => {
      if (s.dataset.display === "flex") {
        s.classList.remove("first:grid", "first:lg:flex");
        s.classList.add("hidden", "lg:hidden");
      }
      if (s.dataset.display === "grid") {
        s.classList.remove("first:grid");
        s.classList.add("hidden");
      }
    });
  }
  setupListeners() {
    this.addEventListener("click", (e) => {
      const button = e.target.closest("button");
      if (button && button.matches("[data-attribute]")) {
        if (
          button.classList.contains("text-gold") ||
          button.classList.contains("first:text-gold")
        ) {
          return;
        }

        const attr = button.dataset.attribute;
        const match = this.querySelector(`[data-nutrient="${attr}"]`);

        this.querySelectorAll("[data-attribute]").forEach((el) => {
          el.classList.remove("text-gold");
        });

        button.classList.add("text-gold");

        this.querySelectorAll("[data-nutrient]").forEach((s) => {
          if (s.dataset.display === "flex") {
            s.classList.remove("flex");
            s.classList.add("hidden");
          }
          if (s.dataset.display === "block") {
            s.classList.remove("block");
            s.classList.add("hidden");
          }
        });

        if (match) {
          if (match.dataset.display === "flex") {
            match.classList.remove("hidden");
            match.classList.add("flex");
          }
          if (match.dataset.display === "block") {
            match.classList.remove("hidden");
            match.classList.add("block");
          }
          this.disableNutrientDesc();
          const first = match.querySelector("button[data-nutrient-desc]");
          first.classList.add("first:font-heading-bold");
          first.classList.remove("first:font-heading-regular");

          this.hideDesc();
          if (match.dataset.display === "flex") {
            match
              .querySelector("[data-desc]")
              .classList.remove("hidden", "lg:hidden");
            match.querySelector("[data-desc]").classList.add("grid", "lg:flex");
          }
          if (match.dataset.display === "block") {
            match.querySelector("[data-desc]").classList.add("first:grid");
          }
        }
      }

      if (button && button.matches("[data-nutrient-desc]")) {
        const desc = button.dataset.nutrientDesc;
        const match = document.querySelector(`[data-desc="${desc}"]`);

        this.disableNutrientDesc();
        button.classList.remove("font-heading-regular");
        button.classList.add("font-heading-bold");

        this.hideDesc();

        if (match) {
          if (match.dataset.display === "flex") {
            match.classList.remove("hidden", "lg:hidden");
            match.classList.add("grid", "lg:flex");
          }
          if (match.dataset.display === "grid") {
            match.classList.remove("hidden");
            match.classList.add("grid");
          }
        }
      }
    });
    this.addEventListener("change", (e) => {
      const select = e.target.closest("select");
      const selectedIndex = select.selectedIndex;
      const selectedOption = select.options[selectedIndex];
      const selectedText = selectedOption.text;
      const commonParent = select.closest(".border-b");
      const span = commonParent.querySelector(".vitamin-name-span");
      const desc = selectedOption.dataset.nutrientDesc;
      const match = this.querySelector(`[data-desc="${desc}"]`);

      this.disableNutrientDesc();
      this.hideDesc();

      if (span) {
        span.innerHTML = selectedText;
      }

      if (match) {
        if (match.dataset.display === "flex") {
          match.classList.remove("hidden");
          match.classList.add("grid", "lg:flex");
        } else {
          match.classList.remove("hidden");
          match.classList.add("grid");
        }
      }
    });
  }
}

customElements.define("nutrients-selector", NutrientSelector);
